import Box from '@mui/material/Box';
import {
  MediaControlBar,
  MediaController,
  MediaFullscreenButton,
  MediaMuteButton,
  MediaPlayButton,
  MediaSeekBackwardButton,
  MediaSeekForwardButton,
  MediaTimeDisplay,
  MediaTimeRange,
  MediaVolumeRange,
} from 'media-chrome/react';
import { useState, type SyntheticEvent } from 'react';

import { useExclusiveVideoPlayback } from './use-exclusive-video-playback';

interface PostVideoPlayerProps {
  src: string;
  // Poster frame shown before playback; the transcoder extracts one per video.
  poster?: string;
  maxHeight: number;
}

/**
 * Renders a post's video attachment with media-chrome controls (play/seek,
 * scrubber, time, volume, fullscreen).
 *
 * In the card the player is sized to a fixed height (`maxHeight`) and hugs the
 * video's own width for that height — a portrait clip stays narrow instead of
 * being pillar-boxed to the full content width — capped at the content width.
 * The height cap lives in CSS (not inline on the element) so the `:fullscreen`
 * rule can drop it; otherwise fullscreen would leave the video pinned small.
 *
 * Playback is exclusive across the page: starting this video pauses any other
 * post video that is playing.
 */
export const PostVideoPlayer = ({ src, poster, maxHeight }: PostVideoPlayerProps) => {
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const { registerVideo, handlePlay } = useExclusiveVideoPlayback();

  const handleLoadedMetadata = (event: SyntheticEvent<HTMLVideoElement>) => {
    const { videoWidth, videoHeight } = event.currentTarget;
    if (videoWidth > 0 && videoHeight > 0) {
      setAspectRatio(videoWidth / videoHeight);
    }
  };

  return (
    <Box
      sx={{
        '& media-controller': {
          height: maxHeight,
          maxWidth: '100%',
          // Once the intrinsic ratio is known, let height + aspect-ratio derive
          // the width so the frame hugs the video. Until then, fall back to full
          // width to avoid a collapsed box on first paint.
          width: aspectRatio ? 'auto' : '100%',
          aspectRatio: aspectRatio ?? 'auto',
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: '#000',
          '--media-primary-color': '#fff',
          '--media-control-bar-display': 'flex',
        },
        // Fullscreen: the feed row this player sits in is transformed (the
        // virtualizer positions rows with translateY), which would make a
        // percentage-sized fullscreen box resolve against that ~400px row. Size
        // with viewport units instead so it fills the screen regardless, pin it
        // to the viewport, and center the (letterboxed) video.
        '& media-controller:fullscreen': {
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100vh',
          maxWidth: 'none',
          aspectRatio: 'auto',
          borderRadius: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#000',
        },
      }}
    >
      <MediaController>
        {/* User-uploaded videos have no caption track. */}
        {/* oxlint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={registerVideo}
          slot="media"
          src={src}
          poster={poster}
          preload="metadata"
          playsInline
          onPlay={handlePlay}
          onLoadedMetadata={handleLoadedMetadata}
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
        <MediaControlBar>
          <MediaPlayButton />
          <MediaSeekBackwardButton seekOffset={10} />
          <MediaSeekForwardButton seekOffset={10} />
          <MediaTimeRange />
          <MediaTimeDisplay showDuration />
          <MediaMuteButton />
          <MediaVolumeRange />
          <MediaFullscreenButton />
        </MediaControlBar>
      </MediaController>
    </Box>
  );
};
